import { useMemo } from 'react';
import { unreadByTask, type Message, type Task } from '@machs/shared';
import { useSync } from '../lib/sync';
import { imageUrl } from '../lib/images';
import { Icon, type IconName } from '../lib/icons';
import { avatarGradient, initials, listTime, shortDate } from '../lib/format';
import { Avatar } from './ui';

/**
 * Einstiegsseite: Zahlen zum aktuellen Stand, zuletzt aktive Aufgaben und ein
 * Protokoll der letzten Beiträge. Über das Logo in der Seitenleiste erreichbar.
 */
export function Dashboard({ businessId, onOpenTask, onNewTask, onProject }: {
  businessId: string | null;
  onOpenTask: (id: string) => void;
  onNewTask: () => void;
  onProject: (id: string) => void;
}) {
  const { client, me, version } = useSync();
  // Bleibt innerhalb einer Sitzung stehen, wechselt beim nächsten Aufruf
  const spruch = useMemo(() => SPRUECHE[Math.floor(Math.random() * SPRUECHE.length)]!, []);

  const projects = useMemo(
    () => [...client.data.project.values()].filter((p) => !p.archivedAt && p.businessId === businessId),
    [client, version, businessId],
  );
  const projectIds = useMemo(() => new Set(projects.map((p) => p.id)), [projects]);

  const tasks = useMemo(
    () => [...client.data.task.values()].filter((t) => projectIds.has(t.projectId)),
    [client, version, projectIds],
  );
  const unread = useMemo(() => unreadByTask(client, me.user.id), [client, version, me.user.id]);

  const heute = new Date();
  heute.setHours(23, 59, 59, 999);
  const vorWoche = new Date(Date.now() - 7 * 86_400_000).toISOString();

  const offen = tasks.filter((t) => t.status !== 'erledigt');
  const faellig = offen.filter((t) => t.dueAt && t.dueAt <= heute.toISOString());
  const meine = offen.filter((t) => t.assigneeIds.includes(me.user.id));
  const ungelesen = offen.filter((t) => (unread.get(t.id) ?? 0) > 0);
  const erledigtWoche = tasks.filter((t) => t.status === 'erledigt' && (t.archivedAt ?? '') >= vorWoche);

  const zuletzt = useMemo(
    () => [...offen].sort((a, b) => (b.lastMessageAt ?? b.createdAt).localeCompare(a.lastMessageAt ?? a.createdAt)).slice(0, 6),
    [offen],
  );

  // Tätigkeitsprotokoll: die letzten Beiträge über alle Aufgaben des Geschäfts
  const verlauf = useMemo(() => {
    const taskById = new Map(tasks.map((t) => [t.id, t]));
    return [...client.data.message.values()]
      .filter((m) => !m.deletedAt && taskById.has(m.taskId))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .slice(0, 8)
      .map((m) => ({ m, task: taskById.get(m.taskId)! }));
  }, [client, version, tasks]);

  return (
    <div className="scroll dash">
      <header className="dash-head">
        <div className="min-w-0">
          {/* Kein Logo: das steht schon in der Seitenleiste */}
          {/* Der Spruch steht direkt hinter der Anrede, leiser gesetzt */}
          <h2 className="m-0 flex flex-wrap items-baseline gap-x-3">
            <span className="display text-[24px]">{gruss()}, {me.user.name.split(' ')[0]}</span>
            <span className="text-[14px] font-normal text-[var(--muted)]">{spruch}</span>
          </h2>
          <p className="m-0 mt-1 text-[13.5px] text-[var(--muted)]">
            <span className="text-[var(--text)]">{heuteLang()}</span>
            {' · '}
            {offen.length === 0 ? 'gerade ist nichts offen' : `${offen.length} ${offen.length === 1 ? 'Aufgabe läuft' : 'Aufgaben laufen'}, ${ungelesen.length} mit neuen Nachrichten`}
          </p>
        </div>
        <button className="btn btn-primary" onClick={onNewTask}><Icon name="plus" size={18} />Neue Aufgabe</button>
      </header>

      <div className="kpis">
        <Kpi label="Offen" value={offen.length} icon="alert" tone="var(--problem)" />
        <Kpi label="Ungelesen" value={ungelesen.length} icon="chats" tone="var(--brand-b)" />
        <Kpi label="Fällig" value={faellig.length} icon="calendar" tone="var(--pruefung)" hint={faellig[0]?.dueAt ? `nächste: ${shortDate(faellig[0].dueAt)}` : 'nichts überfällig'} />
        <Kpi label="Meine" value={meine.length} icon="user" tone="var(--brand-a)" />
        <Kpi label="Diese Woche erledigt" value={erledigtWoche.length} icon="check" tone="var(--loesung)" />
      </div>

      <div className="dash-grid">
        <section className="dash-card">
          <h3><Icon name="chats" size={16} />Zuletzt aktiv</h3>
          {zuletzt.length === 0 ? (
            <p className="m-0 py-4 text-center text-[13.5px] text-[var(--muted)]">Noch keine Aufgabe.</p>
          ) : (
            <ul>
              {zuletzt.map((t) => (
                <li key={t.id}>
                  <button onClick={() => onOpenTask(t.id)}>
                    <TaskBild task={t} />
                    <span className="min-w-0 flex-1">
                      <b className="block truncate text-[14.5px]">{t.title}</b>
                      <small className="block truncate text-[12.5px] text-[var(--muted)]">{t.lastMessagePreview ?? 'Noch keine Nachricht'}</small>
                    </span>
                    {(unread.get(t.id) ?? 0) > 0 && <span className="badge">{unread.get(t.id)}</span>}
                    <time className="flex-none text-[11.5px] text-[var(--dim)]">{listTime(t.lastMessageAt ?? t.createdAt)}</time>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="dash-card">
          <h3><Icon name="wave" size={16} />Was zuletzt passiert ist</h3>
          {verlauf.length === 0 ? (
            <p className="m-0 py-4 text-center text-[13.5px] text-[var(--muted)]">Noch nichts geschehen.</p>
          ) : (
            <ol className="log">
              {verlauf.map(({ m, task }) => (
                <li key={m.id}>
                  <button onClick={() => onOpenTask(task.id)}>
                    <span className="dot" style={{ background: m.kind === 'system' ? 'var(--dim)' : m.isMilestone ? 'var(--milestone)' : 'var(--brand-a)' }} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px]">
                        <b>{m.authorKind === 'system' ? 'System' : m.authorName}</b> {beschreibe(m)}
                      </span>
                      <small className="block truncate text-[12px] text-[var(--muted)]">{task.title}</small>
                    </span>
                    <time className="flex-none text-[11.5px] text-[var(--dim)]">{listTime(m.createdAt)}</time>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>

      {projects.length > 0 && (
        <section className="dash-card">
          <h3><Icon name="folder" size={16} />Projekte</h3>
          <div className="dash-projects">
            {projects.map((p) => {
              const anzahl = offen.filter((t) => t.projectId === p.id).length;
              return (
                <button key={p.id} onClick={() => onProject(p.id)}>
                  <Avatar name={initials(p.name)} gradient={p.color ?? avatarGradient(p.id)} src={imageUrl(p.imageMediaId)} size={26} square />
                  <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium">{p.name}</span>
                  <span className="text-[12px] text-[var(--muted)]">{anzahl}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * Dezente Zeilen zum Tag — jede Sitzung eine andere. Kein Pathos, kein
 * Ausrufezeichen: Handwerk statt Kalenderspruch.
 */
const SPRUECHE = [
  'Ein Haken nach dem anderen.',
  'Kleine Schritte zählen auch.',
  'Was heute rausgeht, fehlt morgen nicht.',
  'Erst sortieren, dann loslegen.',
  'Das Wichtigste zuerst, der Rest findet sich.',
  'Kurz anfangen ist besser als lange planen.',
  'Eine Sache nach der anderen, die dafür ganz.',
  'Erledigt ist besser als perfekt.',
  'Fragen kostet weniger als Rätselraten.',
  'Ordnung im Kopf beginnt auf dem Papier.',
  'Nichts liegen lassen, was in zwei Minuten geht.',
  'Am Ende zählt, was fertig ist.',
  'Gute Arbeit braucht keine Eile, nur Richtung.',
  'Heute ein Stück weiter als gestern.',
  'Aufräumen ist auch Fortschritt.',
];

/** Anrede nach Tageszeit. */
function gruss(): string {
  const h = new Date().getHours();
  return h < 11 ? 'Guten Morgen' : h < 18 ? 'Guten Tag' : 'Guten Abend';
}

/** Heutiges Datum ausgeschrieben, z. B. „Montag, 21. September". */
function heuteLang(): string {
  return new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
}

function Kpi({ label, value, icon, tone, hint }: { label: string; value: number; icon: IconName; tone: string; hint?: string }) {
  return (
    <div className="kpi" style={{ '--tone': tone } as React.CSSProperties}>
      <span className="kpi-icon"><Icon name={icon} size={16} /></span>
      <b className="kpi-val">{value}</b>
      <span className="kpi-label">{label}</span>
      {hint && <small className="kpi-hint">{hint}</small>}
    </div>
  );
}

function TaskBild({ task }: { task: Task }) {
  const { client } = useSync();
  const project = client.data.project.get(task.projectId);
  return (
    <Avatar
      name={initials(project?.name ?? task.title)}
      gradient={project?.color ?? avatarGradient(project?.id ?? task.id)}
      src={imageUrl(project?.imageMediaId)}
      size={30}
      square
    />
  );
}

/** Kurze Beschreibung eines Beitrags für das Protokoll. */
function beschreibe(m: Message): string {
  if (m.kind === 'system') return m.body;
  if (m.isMilestone) return `hat einen Meilenstein gesetzt: ${m.body}`;
  switch (m.kind) {
    case 'bild':
      return 'hat ein Foto geschickt';
    case 'video':
      return 'hat ein Video geschickt';
    case 'sprache':
      return 'hat eine Sprachnachricht geschickt';
    case 'datei':
      return 'hat eine Datei geschickt';
    case 'link':
      return 'hat einen Link geteilt';
    case 'rueckfrage':
      return m.questionOpen ? 'hat eine Rückfrage gestellt' : 'hat eine Rückfrage beantwortet';
    default:
      return m.body ? `schrieb: ${m.body.slice(0, 80)}` : 'hat etwas geschrieben';
  }
}
